'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';


interface NavigationItem {
  id: string;
  label: string;
  href: string;
  tooltip?: string;
}

interface AnchorNavigationProps {
  isOpen?: boolean;
}

const navigationItems: NavigationItem[] = [
  { id: 'hjem', label: 'Hjem', href: '/hjem', tooltip: 'Tilbake til forsiden' },
  { id: 'om-oss', label: 'Om oss', href: '/om-oss', tooltip: 'Les mer om oss' },
  { id: 'vilkar', label: 'Vilkår', href: '/vilkar', tooltip: 'Les våre vilkår' },
  { id: 'personvern', label: 'Personvern', href: '/personvern', tooltip: 'Les vår personvernerklæring' },
];

const AnchorNavigation = ({ isOpen = false }: AnchorNavigationProps) => {
  const pathname = usePathname();
  const [activeSection, setActiveSection] = useState<string>('hjem');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(isOpen);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Only track sections on the landing page
      if (pathname === '/' || pathname === '/hjem') {
        const sections = ['landing-page', 'prosess', 'priskalkulator', 'sikkerhet'];
        const scrollPosition = window.scrollY + 100;

        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const offsetTop = element.offsetTop;
            const offsetHeight = element.offsetHeight;

            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              setActiveSection('hjem');
              break;
            }
          }
        }
      } else {
        // Set active section based on current pathname
        if (pathname === '/vilkar') {
          setActiveSection('vilkar');
        } else if (pathname === '/personvern') {
          setActiveSection('personvern');
        } else if (pathname === '/om-oss') {
          setActiveSection('om-oss');
        } else if (pathname === '/' || pathname === '/hjem') {
          setActiveSection('hjem');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [pathname]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, itemId: string) => {
    setIsMobileMenuOpen(false);
    
    // If clicking "Hjem" from any page, navigate to homepage
    if (itemId === 'hjem') {
      // Let Next.js Link handle the navigation
      return;
    }
    
    // For page routes (starts with /), let Next.js Link handle it
    if (href.startsWith('/')) {
      // Let Next.js Link handle the navigation
      return;
    }
    
    // For anchor links on the same page
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled ? 'bg-background shadow-md' : 'bg-background'
        }`}
      >
        <nav className="mx-auto flex h-[60px] items-center justify-between lg:h-[60px]">
          <div className="flex items-center">
            <Link 
              href="/hjem" 
              className="flex items-center space-x-3 px-5 lg:px-8"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                <span className="font-headline text-2xl font-bold text-primary-foreground">G</span>
              </div>
              <span className="font-headline text-xl font-semibold text-foreground lg:text-2xl">
                Gullklar
              </span>
            </Link>
          </div>

          <div className="hidden items-center space-x-1 pr-8 lg:flex">
            {navigationItems.map((item) => (
              <Link
                key={item.id}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href, item.id)}
                className={`group relative px-4 py-2 font-body text-base font-medium transition-colors duration-250 ${
                  activeSection === item.id
                    ? 'text-primary' : 'text-foreground hover:text-primary'
                }`}
                title={item.tooltip}
              >
                {item.label}
                {activeSection === item.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></span>
                )}
              </Link>
            ))}
            <a
              href="mailto:kontakt@gullklar.no"
              className="ml-4 flex items-center space-x-2 rounded-lg bg-accent px-5 py-2.5 font-cta text-sm font-semibold text-accent-foreground shadow-cta transition-all duration-250 hover:bg-accent/90"
            >
              <Icon name="EnvelopeIcon" size={18} variant="solid" />
              <span>Ring oss</span>
            </a>
          </div>

          <button
            onClick={toggleMobileMenu}
            className="mr-5 flex items-center justify-center p-2 text-foreground lg:hidden"
            aria-label="Toggle menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={28} variant="outline" />
          </button>
        </nav>

        {isMobileMenuOpen && (
          <div className="bg-background shadow-md lg:hidden">
            <div className="flex flex-col space-y-1 px-5 py-4">
              {navigationItems.map((item) => (
                <Link
                  key={item.id}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href, item.id)}
                  className={`rounded-lg px-4 py-3 font-body text-base font-medium transition-colors duration-250 ${
                    activeSection === item.id
                      ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <a
                href="mailto:kontakt@gullklar.no"
                className="mt-2 flex items-center justify-center space-x-2 rounded-lg bg-accent px-5 py-3 font-cta text-base font-semibold text-accent-foreground shadow-cta"
              >
                <Icon name="EnvelopeIcon" size={20} variant="solid" />
                <span>Ring oss nå</span>
              </a>
            </div>
          </div>
        )}
      </header>

      <div className="h-[60px] lg:h-[60px]"></div>
    </>
  );
};

export default AnchorNavigation;